import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { Main, Styled } from './style';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';

type ShopifyProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const requestOptions: selectDataType[] = [
   { id: 1, name: 'Create Order', refId: 'createOrder', isActive: true },
   { id: 2, name: 'Get Orders', refId: 'getOrders' },
   { id: 3, name: 'Get Order By ID', refId: 'getOrderById' },
   { id: 4, name: 'Update Order', refId: 'updateOrder' },
   { id: 5, name: 'Create Customer', refId: 'createCustomer' },
   { id: 6, name: 'Get Customers', refId: 'getCustomers' },
   { id: 7, name: 'Update Customer', refId: 'updateCustomer' },
   { id: 8, name: 'Create Product', refId: 'createProduct' },
   { id: 9, name: 'Get Products', refId: 'getProducts' },
   { id: 10, name: 'Update Product', refId: 'updateProduct' }
];

const functionDocs: { [key: string]: string } = {
   createOrder: [
      'Query: {\n    "email": "qa.orders+001@example.com",\n    "financial_status": "paid",\n    "line_items": [{ "title": "QA Item A", "price": "19.99", "quantity": 1 }],\n    "tags": "qa,function-block"\n  }',
      'Response includes: `id`, `order_number`, `customer`, `line_items`, `total_price`, `financial_status`.'
   ].join('\n'),
   getOrders: [
      'Query: {\n    "status": "any",\n    "limit": 50,\n    "fields": "id,order_number,total_price,financial_status"\n  }',
      'Response includes: `data[]`, `pagination`, `meta`, and each order item includes `id`, `order_number`, `total_price`, `financial_status`.'
   ].join('\n'),
   getOrderById: [
      'Query: {\n    "fields": "id,order_number,total_price,financial_status,customer,line_items",\n    "status": "any"\n  }',
      'Response includes: `data`, `meta`, and order details like `id`, `order_number`, `customer`, `line_items`, `total_price`, `financial_status`.'
   ].join('\n'),
   updateOrder: [
      'Query: {\n    "notify_customer": false,\n    "fields": "id,order_number,financial_status,fulfillment_status,total_price"\n  }',
      'Response includes: `data`, `meta`, and updated order fields such as `id`, `order_number`, `financial_status`, `fulfillment_status`, `total_price`.'
   ].join('\n'),
   createCustomer: [
      'Query: {\n    "email": "qa.customer+001@example.com",\n    "first_name": "QA",\n    "last_name": "Customer",\n    "tags": "qa,function-block"\n  }',
      'Response includes: `id`, `email`, `first_name`, `last_name`, `state`, `created_at`.'
   ].join('\n'),
   getCustomers: [
      'Query: {\n    "limit": 50,\n    "fields": "id,email,first_name,last_name,state"\n  }',
      'Response includes: `data[]`, `pagination`, `meta`, and each customer includes `id`, `email`, `first_name`, `last_name`, `state`.'
   ].join('\n'),
   updateCustomer: [
      'Query: {\n    "fields": "id,email,first_name,last_name,state,tags"\n  }',
      'Response includes: `data`, `meta`, and updated customer fields like `id`, `email`, `first_name`, `last_name`, `state`, `tags`.'
   ].join('\n'),
   createProduct: [
      'Query: {\n    "title": "QA Product A",\n    "vendor": "QA Vendor",\n    "product_type": "QA Type",\n    "tags": "qa,function-block"\n  }',
      'Response includes: `id`, `title`, `handle`, `status`, `variants`, `options`.'
   ].join('\n'),
   getProducts: [
      'Query: {\n    "limit": 50,\n    "fields": "id,title,handle,status,variants",\n    "published_status": "any"\n  }',
      'Response includes: `data[]`, `pagination`, `meta`, and each product includes `id`, `title`, `handle`, `status`, `variants`.'
   ].join('\n'),
   updateProduct: [
      'Query: {\n    "fields": "id,title,handle,status,variants,updated_at"\n  }',
      'Response includes: `data`, `meta`, and updated product fields such as `id`, `title`, `handle`, `status`, `variants`, `updated_at`.'
   ].join('\n')
};

export function Shopify({ id, data, setChildMinimize, setChildSettings }: ShopifyProps) {
   const { setNodes } = useReactFlow();
   const [requests, setRequests] = React.useState<selectDataType[]>(requestOptions);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            request: requestOptions[0].refId,
            payload: '{\n  "title": "Sample"\n}',
            query: '{\n  "limit": 50\n}'
         });
      } else {
         setRequests(prev => prev.map(request => ({ ...request, isActive: request.refId === data.value.request })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   };

   const handleSelectRequest = (selectId: number) => {
      const refId = requests.find(request => request.id === selectId)?.refId;
      setRequests(prev => prev.map(request => ({ ...request, isActive: request.id === selectId })));
      updateNodeValue(id, setNodes, { request: refId });
   };

   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   };

   const activeRequest = data.value?.request || requests.find(request => request.isActive)?.refId || requestOptions[0].refId;
   const isOrderRequest = ['getOrderById', 'updateOrder'].includes(activeRequest);
   const isCustomerRequest = ['updateCustomer'].includes(activeRequest);
   const isProductRequest = ['updateProduct'].includes(activeRequest);
   const isPayloadRequest = ['createOrder', 'updateOrder', 'createCustomer', 'updateCustomer', 'createProduct', 'updateProduct'].includes(activeRequest);
   const isQueryRequest = ['getOrders', 'getCustomers', 'getProducts', 'getOrderById'].includes(activeRequest);
   const isWarning = !data.value?.connection;

   return (
      <>
         <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
         <Main>
            {isWarning ? (
               <>
                  <div id="warning"></div>
                  <span id="warningIcon"><IoWarningOutline size={ 21 } /></span>
               </>
            ) : data.output ? (
               <>
                  <div id="ran"></div>
                  <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
               </>
            ) : null}

            {isExpanded ? (
               <ExpandedBlock
                  data={{ id, ...data }}
                  handleClose={ () => setIsExpanded(false) }
                  isWarning={ isWarning }
                  style={{ minWidth: '420px' }}
               >
                  <Styled>
                     <div className="action">
                        <AuthenticateSelect
                           type="shopify"
                           defaultId={ data.value?.connection?.id }
                           handleAuthenticate={ handleAuthenticate }
                           background={ data.background }
                        />
                     </div>

                     <Select title="Request" data={ requests } handleSelectItem={ handleSelectRequest } isSmall={ true } />

                     {isOrderRequest ? (
                        <TextInput
                           keyRef="orderId"
                           value={ data.value?.orderId }
                           title="Order ID"
                           placeholder="450789469"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isCustomerRequest ? (
                        <TextInput
                           keyRef="customerId"
                           value={ data.value?.customerId }
                           title="Customer ID"
                           placeholder="207119551"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isProductRequest ? (
                        <TextInput
                           keyRef="productId"
                           value={ data.value?.productId }
                           title="Product ID"
                           placeholder="632910392"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isPayloadRequest ? (
                        <Textarea
                           title="Payload"
                           keyRef="payload"
                           value={ data.value?.payload }
                           placeholder={'{\n  "title": "Sample"\n}'}
                           handleChange={ (e) => updateNodeValue(id, setNodes, { payload: e.target.value }) }
                        />
                     ) : null}

                     {isQueryRequest ? (
                        <Textarea
                           title="Query"
                           keyRef="query"
                           value={ data.value?.query }
                           placeholder={'{\n  "limit": 50,\n  "fields": "id,name"\n}'}
                           handleChange={ (e) => updateNodeValue(id, setNodes, { query: e.target.value }) }
                        />
                     ) : null}

                     <div className="previewSection">
                        <h5>Function Block Preview</h5>
                        <pre>{ functionDocs[activeRequest] || 'No function metadata available.' }</pre>
                     </div>
                  </Styled>
               </ExpandedBlock>
            ) : null}
         </Main>
      </>
   );
}